import { FilterOutlined, ReloadOutlined, CalendarOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Button, Card, Form, Input, Space, Table, Select, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { AdminLog, AdminLogsResponse } from '../../types/logs.types';
import { handleAPIError } from '../../utils/helpers';
import CopyableText from '../../components/copyable-text';

type LogCategory = 'critical' | 'device' | 'user' | 'credit' | 'config' | 'other';

interface ParsedLog {
    category: LogCategory;
    label: string;
    description: React.ReactNode;
    color: string;
}

function parseLog(action: string): ParsedLog {
    // Critical device actions
    if (/manage device action 'reset'/i.test(action) || /device action 'reset'/i.test(action)) {
        const imei = action.match(/IMEI (\S+)|device (\S+)/)?.[1] ?? '';
        return { category: 'critical', label: 'RESET DEVICE', color: 'red',
            description: <span>Factory reset sent to <code className="font-mono text-xs">{imei}</code></span> };
    }
    if (/manage device action 'unsuspend_google_play'/i.test(action)) {
        const imei = action.match(/IMEI (\S+)/)?.[1] ?? '';
        return { category: 'device', label: 'Unsuspend Google Play', color: 'orange',
            description: <span>Unsuspended Google Play on <code className="font-mono text-xs">{imei}</code></span> };
    }
    if (/manage device action 'clear_zazu_cache'/i.test(action)) {
        const imei = action.match(/IMEI (\S+)/)?.[1] ?? '';
        return { category: 'device', label: 'Clear App Cache', color: 'orange',
            description: <span>Cleared Zazu Mobile cache on <code className="font-mono text-xs">{imei}</code></span> };
    }
    // Device lock/unlock
    const lockMatch = action.match(/device action '(lock|unlock)' sent for device (\S+)/i);
    if (lockMatch) {
        const [, act, imei] = lockMatch;
        const isLock = act === 'lock';
        return { category: 'device', label: isLock ? 'Lock Device' : 'Unlock Device', color: isLock ? 'orange' : 'green',
            description: <span>{isLock ? 'Locked' : 'Unlocked'} device <code className="font-mono text-xs">{imei}</code></span> };
    }
    // Legacy lock/unlock format
    if (/^locked device/i.test(action)) {
        const imei = action.replace(/locked device/i, '').trim();
        return { category: 'device', label: 'Lock Device', color: 'orange',
            description: <span>Locked device <code className="font-mono text-xs">{imei}</code></span> };
    }
    if (/^unlocked device/i.test(action)) {
        const imei = action.replace(/unlocked device/i, '').trim();
        return { category: 'device', label: 'Unlock Device', color: 'green',
            description: <span>Unlocked device <code className="font-mono text-xs">{imei}</code></span> };
    }
    // Set lease date
    const leaseMatch = action.match(/device action 'set_lease_date \((.+?)\)' sent for device (\S+)/i);
    if (leaseMatch) {
        const [, date, imei] = leaseMatch;
        return { category: 'device', label: 'Set Lease Date', color: 'blue',
            description: <span>Set lease date to <strong>{date}</strong> on <code className="font-mono text-xs">{imei}</code></span> };
    }
    // User status change
    const statusMatch = action.match(/changed account info of (.+?) - status: (.+?) -> (.+)/i);
    if (statusMatch) {
        const [, phone, from, to] = statusMatch;
        const toTrim = to.trim();
        const isVerified = toTrim === 'verified';
        return { category: 'user', label: 'Status Change', color: isVerified ? 'green' : 'purple',
            description: <span>Changed <code className="font-mono text-xs">{phone}</code> from <Tag bordered={false} color="default">{from.trim()}</Tag> → <Tag bordered={false} color={isVerified ? 'green' : 'blue'}>{toTrim}</Tag></span> };
    }
    // Credit score
    const creditMatch = action.match(/ran credit score for user (\d+).*?— MNO: (.+?), CRB: (.+)/i);
    if (creditMatch) {
        const [, userId, mno, crb] = creditMatch;
        return { category: 'credit', label: 'Credit Score', color: 'cyan',
            description: <span>Credit score for user <strong>#{userId}</strong> — MNO: <strong>{mno}</strong>, CRB: <strong>{crb}</strong></span> };
    }
    // IMEI update
    const imeiUpdateMatch = action.match(/updated imei for device (\d+) from (\S+) to (\S+)/i);
    if (imeiUpdateMatch) {
        const [, deviceId, oldImei, newImei] = imeiUpdateMatch;
        return { category: 'device', label: 'IMEI Update', color: 'blue',
            description: <span>Device <strong>#{deviceId}</strong> IMEI changed from <code className="font-mono text-xs">{oldImei}</code> → <code className="font-mono text-xs">{newImei}</code></span> };
    }
    // Installment payment
    const paymentMatch = action.match(/initiated installment payment for installment (\d+) for device (\S+)/i);
    if (paymentMatch) {
        const [, installmentId, imei] = paymentMatch;
        return { category: 'config', label: 'Payment', color: 'green',
            description: <span>Installment <strong>#{installmentId}</strong> payment for device <code className="font-mono text-xs">{imei}</code></span> };
    }
    // Payout
    const payoutMatch = action.match(/initiated payout for device (\d+) with amount (.+)/i);
    if (payoutMatch) {
        const [, deviceId, amount] = payoutMatch;
        return { category: 'config', label: 'Payout', color: 'gold',
            description: <span>Payout of <strong>UGX {Number(amount).toLocaleString()}</strong> for device <strong>#{deviceId}</strong></span> };
    }
    // Verification call
    const verifMatch = action.match(/(updated|created) verification call for user (\d+)/i);
    if (verifMatch) {
        const [, verb, userId] = verifMatch;
        return { category: 'user', label: 'Verification Call', color: 'purple',
            description: <span>{verb.charAt(0).toUpperCase() + verb.slice(1).toLowerCase()} verification call for user <strong>#{userId}</strong></span> };
    }
    // Device comment/update (most common — e.g. "Updated device 12345. Payload: {comments: ...}")
    const deviceUpdateMatch = action.match(/updated device (\S+)\.\s*payload:\s*(.+)/i);
    if (deviceUpdateMatch) {
        const [, deviceId, payloadRaw] = deviceUpdateMatch;
        let summary = '';
        try {
            const payload = JSON.parse(payloadRaw);
            if (payload.comments) {
                const firstLine = String(payload.comments).split('\n')[0].trim();
                summary = firstLine.length > 80 ? firstLine.slice(0, 80) + '…' : firstLine;
            } else {
                summary = Object.keys(payload).join(', ');
            }
        } catch {
            summary = payloadRaw.slice(0, 80);
        }
        return { category: 'other', label: 'Device Update', color: 'geekblue',
            description: <span>Device <strong>{deviceId}</strong> — {summary}</span> };
    }
    // Config
    if (/config/i.test(action)) {
        return { category: 'config', label: 'Config', color: 'purple',
            description: <span>{action}</span> };
    }
    // Fallback
    return { category: 'other', label: 'Action', color: 'geekblue',
        description: <span>{action}</span> };
}

interface AdminUser {
	id: number;
	first_name: string;
	last_name: string;
	phone_number: string;
}

interface FilterValues {
	search_term: string | null;
	admin: number | null;
	category: LogCategory | null;
}

const AdminLogs = () => {
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 100,
	});
	const [count, setCount] = useState(0);
	const [filters, setFilters] = useState<FilterValues>({
		search_term: null,
		admin: null,
		category: null,
	});

	// Fetch admin users
	const { data: adminUsersData, isLoading: adminUsersLoading } = useQuery({
		queryKey: ['admin_users'],
		queryFn: async () => {
			const response = await axios.get('/admin/users', {
				withCredentials: true,
				params: {
					page: 1,
					limit: 100,
					role: 'admin',
				},
			});
			return response.data.results as AdminUser[];
		},
	});

	const { data, error, isLoading } = useQuery({
		queryKey: ['admin_logs', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params: Record<string, string | number> = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
			};

			if (filters.search_term) {
				params.search_term = filters.search_term;
			}
			if (filters.admin) {
				params.admin = filters.admin;
			}

			const response = await axios.get<AdminLogsResponse>('/admin/communications/logs', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const allLogs: AdminLog[] = data?.results || [];
	const logs = filters.category
		? allLogs.filter((log) => parseLog(log.action).category === filters.category)
		: allLogs;

	const columns: ColumnsType<AdminLog> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 60,
			sorter: (a, b) => a.id - b.id,
		},
		{
			title: 'Admin',
			dataIndex: 'admin',
			key: 'admin',
			render: (admin) => (
				<div className="flex items-center gap-2">
					<Avatar
						src={admin.dp}
						size="small"
					/>
					<div>
						<span className="text-sm font-medium uppercase">
							{admin.first_name} {admin.last_name}
						</span>
						<CopyableText text={admin.phone_number}>
							<a
								className="text-xs text-gray-500"
								href={`tel:+${admin.phone_number}`}
							>
								{admin.phone_number}
							</a>
						</CopyableText>
					</div>
				</div>
			),
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			render: (action) => {
				const parsed = parseLog(action);
				return (
					<div className="flex flex-col gap-1">
						<Tag color={parsed.color} className="w-fit">{parsed.label}</Tag>
						<span className="text-sm text-gray-700">{parsed.description}</span>
					</div>
				);
			},
		},
		{
			title: 'Date & Time',
			dataIndex: 'create_at',
			key: 'create_at',
			width: 200,
			render: (date) => (
				<div className="flex items-center gap-1">
					<CalendarOutlined className="text-gray-400" />
					<span className="text-sm">{dayjs(date).format('DD MMM YYYY - LT')}</span>
				</div>
			),
			sorter: (a, b) => dayjs(a.create_at).unix() - dayjs(b.create_at).unix(),
		},
	];

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			search_term: null,
			admin: null,
			category: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	const adminOptions = (adminUsersData || []).map((admin) => ({
		value: admin.id,
		label: `${admin.first_name} ${admin.last_name} (${admin.phone_number})`,
	}));

	return (
		<AppLayout
			title="Admin Logs"
			subtitle="View and track all admin actions and communications."
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={filters}
				>
					<div className="flex flex-wrap items-end gap-4">
						<Form.Item
							name="search_term"
							label="Search Action"
							className="mb-0 min-w-[250px]"
						>
							<Input
								placeholder="Search by action (e.g., 'config', 'user')"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="admin"
							label="Admin"
							className="mb-0 min-w-[300px]"
						>
							<Select
								placeholder="Select admin"
								allowClear
								options={adminOptions}
								loading={adminUsersLoading}
							/>
						</Form.Item>

						<Form.Item
							name="category"
							label="Category"
							className="mb-0 min-w-[180px]"
						>
							<Select
								placeholder="All categories"
								allowClear
								options={[
									{ value: 'critical', label: '🔴 Critical' },
									{ value: 'device', label: '📱 Device Action' },
									{ value: 'user', label: '👤 User' },
									{ value: 'credit', label: '📊 Credit Score' },
									{ value: 'config', label: '⚙️ Config' },
									{ value: 'other', label: '🔵 Other' },
								]}
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
							</Space>
						</Form.Item>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={logs}
					rowKey="id"
					rowClassName={(record) => parseLog(record.action).category === 'critical' ? 'bg-red-50' : ''}
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
					scroll={{ x: 1000 }}
				/>
			</div>
		</AppLayout>
	);
};

export default AdminLogs;
